import React, { useContext, useEffect, useState} from 'react';
import SearchFilter from './SearchFilter';
import { Direction, FilterPage, InitSearchFilter, initUserDevices, ISearchFilter, IUserDevices, OrderBy, Toggle } from  '@/interfaces/insightsp2';
import UsersDevices from './UsersDevices';
import ContentTable from './ContentTable';
import { container, TYPES } from "@/inversifyconfig/container.config";
import { IContentTable, initContentTable } from  '@/interfaces/IContentTable';
import { IInsightsContext, InsightsContext } from '@/context/InsightsContext';
import { IApiSettings, IHttpRequest } from  '@/interfaces/IHttp';
import { columnsUserGroupLogin } from '@/data/insightsColumns';
import { addRankings, buildCompareDownloadLink } from  '@/helpers/Insights';
import DateLabelBuilder from './DateLabelBuilder';
import ToolTipWrapper from '../Tooltip/ToolTipWrapper';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import  '@/css/insightsUsers.scss';
import GenError from './GenError';
import { removeTimeZone } from  '@/helpers/Dates';
import {tooltipData} from '../../../data/insights';

const Users = () => {

    // Context 
    const context:IInsightsContext = useContext(InsightsContext);

    // Search filter 
    const [searchFilterOptions, setSearchFilterOptions] = useState<ISearchFilter>(InitSearchFilter);

    // User devices
    const [userDevicesDataDay1, setUserDevicesDataDay1] = useState<IUserDevices>(initUserDevices);
    const [userDevicesDataDay2, setUserDevicesDataDay2] = useState<IUserDevices>(initUserDevices);

    // User group login
    const [userGroupToggle, setUserGroupToggle] = useState<Toggle>(Toggle.date1);
    const [userGroupOrderBy, setUserGroupOrderBy] = useState<OrderBy>({column:"loggedInPercentage", direction:Direction.dsc});
    const [userGroup, setUserGroup] = useState<IContentTable>(initContentTable);
    const [userGroupDay1, setUserGroupDay1] = useState<any[]>([]);
    const [userGroupDay2, setUserGroupDay2] = useState<any[]>([]);

    // Loading
    const [initLoad, setInitLoad] = useState<boolean>(true);
    const [genError, setGenError] = useState<boolean>(false);

    // API
    const apiSettings = container.get<IApiSettings>(TYPES.IApiSettings);

    // Init
    useEffect(()=> {

        context.loadTracker.setLoadMin(2);   
        context.setFilterPage(FilterPage.users);

        startLoading();

        search(false);
    }, []);

    // Gen error hide loading
    useEffect(() => {
        if (genError){
            context.setLoading(false);
        }
    }, [genError]);

    // Order by
    useEffect(() => {
        if (!initLoad){

            context.setLoading(true)

            search(true).then(() => context.setLoading(false));
        }    
    }, [userGroupOrderBy]);

    useEffect(() => {

        if(!initLoad){

            const dataUserGroup = userGroupToggle === Toggle.date1 ? userGroupDay1: userGroupDay2;

            setUserGroup(prevState => ({
                ...prevState,
                data: dataUserGroup,
                csvDownload: urlBuilder(userGroupToggle === Toggle.date1, `${apiSettings.apiBaseUrl()}insights/user-group-login-csv`)
            }));
        }

    }, [userGroupToggle]);
    
    useEffect(() => {

        if (!initLoad){

            startLoading();

            search(false);
        }     
    }, [searchFilterOptions]);

    async function search(groupOnly:boolean){

        // User devices
        let devices;
        if (!groupOnly){
            devices = getData(`insights/users-and-devices`).then(({day1Data, day2Data}) => {
        
                setUserDevicesDataDay1(day1Data);
                setUserDevicesDataDay2(day2Data);
        
            }).catch((error) => {
                console.log(`User devices error ${error}`);
            })    
        }
        else{
            devices = Promise.resolve();
        }

        // User groups          
        const login = getData(`insights/user-group-login`).then(({day1Data, day2Data}) => {

            // Rankings
            const day2WithModal = day2Data.data.map((item,idx)=> ({ ...item, id: {directionStats:  {  leaderBoardPosition:idx+1, itemId:item.id} } }));
            const day1WithRankings = addRankings(day1Data.data, day2WithModal);
        
            const day1 = [...setOrder(day1WithRankings)];
            const day2 = [...setOrder(day2WithModal)];

            setUserGroupDay1(day1);
            setUserGroupDay2(day2);

            const currentData = userGroupToggle === Toggle.date1 ? day1 : day2;

            setUserGroup({
                title:"",
                data:currentData,
                columns:columnsUserGroupLogin(),
                csvDownload: urlBuilder(!!Toggle.date1, `${apiSettings.apiBaseUrl()}insights/user-group-login-csv`)
            });
    
        }).catch((error) => {
            console.log(`User devices error ${error}`);
            setGenError(true);
        }) 

        await Promise.all([devices, login]);

        setInitLoad(false);
    }

    async function getData(controller:string){

        let day1Data, day2Data;

        // Day 1
        const day1 = container.get<IHttpRequest>(TYPES.IHttpRequest).get(urlBuilder(true, controller)).then((x) => {

            day1Data = x.data;

        }).catch((error) => {
            console.log(`Error day 1: ${error}`);
        });

        // Day 2
        const day2 = container.get<IHttpRequest>(TYPES.IHttpRequest).get(urlBuilder(false, controller)).then((x) => {

            day2Data = x.data;
            
        }).catch((error) => {
            console.log(`Error day 2: ${error}`);
        });

        await Promise.all([day1, day2])

        return {day1Data, day2Data}
    }

    function setOrder(data):any[] {

        const reOrdered = [];

        data.map(x => {
            reOrdered.push(
                {
                    id:x.id,
                    title:x.title,
                    userCount:x.userCount,
                    loggedInPercentage: `${x.loggedInPercentage}%`
                }
            )
        })

        return reOrdered;
    }

    function urlBuilder(day1:boolean, controller:string):string {
  
        const {date1, date2, selectedUserGroups, selectedWorkspace} = searchFilterOptions;

        let url = `${controller}`;

        // Dates
        if (day1){
            url += `?dateFrom=${removeTimeZone(date1.dateFrom)}&dateTo=${removeTimeZone(date1.dateTo)}`;
        }
        else {
            url += `?dateFrom=${removeTimeZone(date2.dateFrom)}&dateTo=${removeTimeZone(date2.dateTo)}`;
        }

        // Order by
        url += `&orderBy=${userGroupOrderBy.column}&direction=${userGroupOrderBy.direction}`;

        // Workspace
        if (selectedWorkspace !== null){
            url+= `&workspaceGuids=${selectedWorkspace.value}`
        }

        // Groups
        if (selectedUserGroups.length){    
            selectedUserGroups.forEach((g) => {
                url+= `&userGroupIds=${g.value}`;
            });
        }
        
        return url
    }

    function startLoading():void {
        context.loadTracker.dispatch({type: 'reset'})
        context.setLoading(true);
    }

    function downloadLink():string {   
        return `${apiSettings.apiBaseUrl()}insights/users-and-devices-comparison-csv?${buildCompareDownloadLink(searchFilterOptions)}`;
    }

    // Show nothing until has data
    if (initLoad){
        return null;
    }

    // Gen error
    if (genError){
        return (
            <GenError />
        )
    }

    return (
        <div className="insights-usersContainer">
                 
            <SearchFilter 
                page={FilterPage.users} 
                setSearchFilterOptions={setSearchFilterOptions}
            />

            <UsersDevices data={userDevicesDataDay1} dataDay2={userDevicesDataDay2} compareDownloadLink={downloadLink()} />

        
            <div className="toggle-container">
                        
                <h2 className="table-header">User Groups Login Overview</h2>

                <ToolTipWrapper id="user-group-login" className="user-group-login" >                     
                    <ReactTooltip place="top" className='thrive-tooltip' delayHide={1000}  id="user-group-login" >
                        <span>{tooltipData.userGroupsLoginOverview}</span>
                    </ReactTooltip>
                </ToolTipWrapper>

                <section className="toggle">

                    <span className="title">TOGGLE DATE</span>

                    <div onClick={() => setUserGroupToggle(Toggle.date1)} className={userGroupToggle === Toggle.date1 ? 'selected':''}>
                        <DateLabelBuilder toggle={Toggle.date1} searchFilterOptions={searchFilterOptions} />
                    </div>
                    
                    <div onClick={() => setUserGroupToggle(Toggle.date2)} className={userGroupToggle === Toggle.date2 ? 'selected':''}>
                        <DateLabelBuilder toggle={Toggle.date2} searchFilterOptions={searchFilterOptions} />
                    </div>

                </section>

                <div className="center-container">
                    <ContentTable content={userGroup} className="content-table-4" setOrder={setUserGroupOrderBy} defaultOrder={userGroupOrderBy} detailsModal={true} />
                </div>

            </div>
             
            
        </div>
    );
};

export default Users;